/**
 * Flexible Bundle Builder
 * Modern, clean implementation for collection-based bundle building
 */

// CSS Styles (layout-only; defer visual language to theme CSS)
const STYLES = `
  .flexible-bundle {
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem;
  }

  .bundle-configurator {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
    margin-bottom: 2rem;
  }

  .product-selector {
    background: var(--color_bg, #fff);
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid rgba(var(--color-foreground, 0, 0, 0), 0.1);
    transition: all 0.2s ease-in-out;
  }

  .product-selector.active {
    border-color: rgba(var(--color-foreground, 0, 0, 0), 0.6);
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  }

  .selector-title {
    font-size: 1.2rem;
    font-weight: bold;
    margin-bottom: 1rem;
    color: var(--color_text, #000);
  }

  .form-group {
    margin-bottom: 1rem;
  }

  .form-group label {
    display: block;
    font-weight: 700;
    margin-bottom: 0.5rem;
    color: var(--color_text, #000);
  }

  /* Select styling is inherited from theme via .option-select */

  /* Image dropdown (custom select with thumbnails) */
  .image-dropdown { position: relative; width: 100%; }
  .image-dropdown__toggle {
    width: 100%;
    display: flex; align-items: center; justify-content: space-between;
    gap: 0.75rem; padding: 0.6rem 0.75rem;
    background: var(--color_form, #fff);
    border: 1px solid var(--color_form_border, #ccc);
    border-radius: 6px; cursor: pointer;
  }
  .image-dropdown.open .image-dropdown__toggle {
    border-color: rgba(var(--color-foreground, 0, 0, 0), 0.6);
    box-shadow: 0 0 0 3px rgba(0,0,0,0.06);
    border-bottom-left-radius: 0; border-bottom-right-radius: 0;
  }
  .image-dropdown__toggle .thumb { width: 48px; height: 27px; object-fit: cover; border-radius: 6px; }
  .image-dropdown__toggle.disabled { opacity: .6; cursor: not-allowed; }
  .image-dropdown__menu {
    position: absolute; left: 0; top: calc(100% - 1px); width: 100%; z-index: 40;
    background: var(--color_form, #fff); border: 1px solid var(--color_form_border, #ddd); border-top: 0;
    border-bottom-left-radius: 10px; border-bottom-right-radius: 10px; border-top-left-radius: 0; border-top-right-radius: 0;
    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
    max-height: 320px; overflow: auto; display: none; padding: 0; box-sizing: border-box;
  }
  .image-dropdown.open .image-dropdown__menu { display: block; }
  .image-dropdown__search { position: sticky; top: 0; background: var(--color_form, #fff); border-bottom: 1px solid var(--color_form_border, #ddd); padding: 0; border-top-left-radius: 10px; border-top-right-radius: 10px; overflow: hidden; }
  .image-dropdown__search .search-field { position: relative; width: 100%; }
  .image-dropdown__search .search-field .icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); width:18px; height:18px; color: var(--color_text, #000); opacity:.5; pointer-events:none; }
  .image-dropdown__search-input { width: 100%; max-width: none; height: 48px; padding: 0 12px 0 38px; border: none; border-radius: 0; font-size: 0.95rem; background: var(--color_form, #fff); color: var(--color_text, #000); box-sizing: border-box; }
  .image-dropdown__search-input::placeholder { color: rgba(0,0,0,0.5); }
  .image-dropdown__search-input:focus { outline: none; box-shadow: inset 0 0 0 2px rgba(0,0,0,0.06); }
  .image-dropdown__options { max-height: inherit; }
  .image-option { display: flex; align-items: center; gap: 0.75rem; padding: 0.65rem 0.85rem; cursor: pointer; }
  .image-option + .image-option { border-top: 1px solid rgba(0,0,0,0.06); }
  .image-option[aria-selected="true"], .image-option:hover { background: rgba(0,0,0,0.04); }
  .image-option .thumb { width: 64px; height: 36px; object-fit: cover; border-radius: 6px; }
  .image-option .label { font-weight: 600; color: var(--color_text, #000); }
  .image-option .price { margin-left: auto; display:flex; align-items:center; gap:.35rem; font-size:.9rem; }
  .image-option .price .old-price { color:#888; text-decoration: line-through; font-weight:500; }
  .image-option .price .new-price { color:#4AA339; font-weight:700; }

  /* Generic price formatting for breakdown */
  .old-price { color:#8a8a8a; text-decoration: line-through; margin-right: .35rem; }
  .new-price { color:#4AA339; font-weight: 700; }

  .keycap-kits {
    margin-top: 1rem;
  }

  .kit-checkbox {
    display: flex;
    align-items: center;
    padding: 0.5rem;
    margin-bottom: 0.5rem;
    border-radius: 6px;
    transition: background-color 0.2s ease;
  }

  .kit-checkbox:hover {
    background: #f0f0f0;
  }

  .kit-checkbox input {
    margin-right: 0.75rem;
  }

  .kit-checkbox.required {
    background: #fff3cd;
    border: 1px solid #ffc107;
  }

  .kit-checkbox.base-kit {
    background: transparent;
    border: 0;
    font-weight: 600;
  }

  .bundle-preview { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem; margin-bottom: 2rem; }
  @media (min-width: 900px) { .bundle-preview { grid-template-columns: repeat(4, 1fr); } }
  .product-card { border-radius: 12px; overflow: hidden; background-color: rgb(var(--color-base-background-1)); border: 1px solid rgba(var(--color-foreground), 0.1); padding-bottom: 0; text-align: left; display: flex; flex-direction: column; }
  .product-card__image { width: 100%; aspect-ratio: 16/9; object-fit: cover; }
  .product-card__info { padding: 1rem; }
  .product-card__title { font-size: 1.1rem; margin: 0.25rem 0 0; color: var(--color_text, #000); }
  .product-card__variant { color: #999; font-size: 0.95rem; margin-top: 0.25rem; }
  .product-card__price { display:flex; gap:.4rem; align-items:center; margin-top: 0.25rem; }
  .product-card__price .old-price { color:#888; text-decoration: line-through; font-size:.95rem; }
  .product-card__price .new-price { color:#4AA339; font-weight:700; }

  .bundle-summary {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 2rem;
  }

  .summary-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 1rem;
  }

  .summary-table th,
  .summary-table td {
    text-align: left;
    padding: 0.5rem 0;
    border-bottom: 1px solid #eee;
  }

  .summary-table th {
    font-weight: 600;
    color: #333;
  }

  .summary-table td:last-child {
    text-align: right;
  }

  .subtotal-row {
    font-weight: bold;
    font-size: 1.1rem;
    border-bottom: none;
  }

  .subtotal-row td {
    font-size: 1.1rem;
  }

  .total-row {
    font-weight: bold;
    font-size: 1.1rem;
    border-bottom: none;
  }

  .discount-row {
    color: #4AA339;
  }
  .discount-row td {
    color: #4AA339;
    font-weight: 600;
  }
  .discount-row .discount-value {
    color: #4AA339;
    font-weight: 600;
  }
  
  .bundle-actions-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1rem;
    margin-top: 1rem;
  }

  .savings-message {
    color: #4AA339;
    font-weight: 600;
    font-size: 1.1rem;
    border: 2px solid #4AA339;
    border-radius: 6px;
    padding: 1rem;
    text-align: center;
    max-width: 450px;
  }
  
  .add-to-cart-button { 
    max-width: 350px;
    font-size: 1.25rem;
  }

  .validation-message {
    background: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 6px;
    padding: 1rem;
    margin-bottom: 1rem;
    color: #856404;
  }

  @media (max-width: 768px) {
    .bundle-configurator {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }

    .bundle-preview {
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    }
  }
`;

// Styles extracted to assets/flexible-bundle.css (kept minimal inline fragment for backward compat)

/**
 * Utility functions
 */
const Utils = {
  formatMoney: (cents) => `$${(cents / 100).toFixed(2)}`,

  // Price helpers (all values are cents)
  formatMoneyCents: (cents) => `$${(cents / 100).toFixed(2)}`,
  renderPricePair: (oldCents, newCents) => {
    if (newCents == null || newCents === oldCents) {
      return `<span class="price">${Utils.formatMoneyCents(oldCents)}</span>`;
    }
    return `<span class="price"><span class="old-price">${Utils.formatMoneyCents(oldCents)}</span><span class="new-price">${Utils.formatMoneyCents(newCents)}</span></span>`;
  },
  
  debounce: (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },

  generateBundleId: (baseId) => `${baseId}-${Date.now()}`,
  
  hasBaseKit: (variants, product) => {
  // If product only has one variant, treat it as base
  if (product && (product.variants || []).length === 1) return true;

  return variants.some(variant => 
    variant.title && variant.title.toLowerCase().includes('base')
  );
},

  isQualifyingKeycapProduct: (product) => {
    const variants = product.variants || [];
    const available = variants.filter(v => v.available);

    if (available.length === 0) {
      return false;
    }

    if (variants.length === 1) {
      const title = (variants[0].title || '').toLowerCase();
      return available.length === 1 && (title.includes('default') || title.includes('base'));
    }

    // For multi-variant sets, require an in-stock base kit
    return available.some(v => (v.title || '').toLowerCase().includes('base'));
  },

  // Check if a variant has enough inventory for the requested quantity
  hasEnoughInventory: (variant, requiredQty) => {
    if (!variant.available) return false;
    if (variant.inventory_policy === 'continue') return true;
    if (typeof variant.inventory_quantity === 'undefined') return variant.available;
    return variant.inventory_quantity >= requiredQty;
  },

  // Standard rounding to match Shopify's cart behavior
  roundCents: (cents) => {
    return Math.round(cents);
  },

  computeOldNew: (priceCents, compareAtCents, discountPercent, quantity = 1) => {
    const baseOld = compareAtCents && compareAtCents > priceCents ? compareAtCents : priceCents;
    // Shopify applies discount to the total line (price × quantity), then rounds
    const lineTotal = priceCents * quantity;
    const discountedLineTotal = lineTotal * (1 - (discountPercent || 0) / 100);
    const newPrice = Utils.roundCents(discountedLineTotal);
    return {
      oldCents: Math.max(0, baseOld * quantity),
      newCents: Math.max(0, newPrice)
    };
  },

  buildImageDropdown: (root, { items, placeholder = 'Select...', onSelect, secondaryTextKey, suppressPrice = false, searchable = false, searchPlaceholder = 'Search...', quantityMultiplier = 1 }) => {
    const selected = { value: '', item: null };
    root.classList.add('image-dropdown');
    root.setAttribute('role', 'listbox');
    root.innerHTML = `
      <button type="button" class="image-dropdown__toggle" aria-haspopup="listbox" aria-expanded="false">
        <div style="display:flex;align-items:center;gap:.75rem">
          <img class="thumb" src="" alt="" style="display:none"/>
          <span class="label">${placeholder}</span>
        </div>
        <span class="chev">▾</span>
      </button>
      <div class="image-dropdown__menu" role="listbox"></div>
    `;
    const toggle = root.querySelector('.image-dropdown__toggle');
    const label = root.querySelector('.label');
    const thumb = root.querySelector('.thumb');
    const menu = root.querySelector('.image-dropdown__menu');
    let focusIndex = -1;

    const setSelected = (item) => {
      selected.value = item?.value || '';
      selected.item = item || null;
      label.textContent = item ? item.label : placeholder;
      if (item && item.imageUrl) {
        thumb.src = item.imageUrl;
        thumb.style.display = 'block';
      } else {
        thumb.style.display = 'none';
      }
    };

    // Build menu structure (optional search + options list)
    menu.innerHTML = `
      ${searchable ? `<div class=\"image-dropdown__search\">
          <div class=\"search-field\">
            <svg class=\"icon\" viewBox=\"0 0 24 24\" aria-hidden=\"true\" focusable=\"false\"><path fill=\"currentColor\" d=\"M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5Zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14Z\"></path></svg>
            <input type=\"text\" class=\"image-dropdown__search-input\" placeholder=\"${searchPlaceholder}\" aria-label=\"${searchPlaceholder}\">
          </div>
        </div>` : ''}
      <div class="image-dropdown__options"></div>
    `;
    const optionsContainer = menu.querySelector('.image-dropdown__options');

    const renderOptionHtml = (it, idx) => {
      const calc = Utils.computeOldNew(it.price || 0, it.compareAt || 0, window.flexibleBundleData.discountPercentage || 0, quantityMultiplier);
      const hasCompare = (it.compareAt && it.compareAt > it.price);
      const old = hasCompare ? (it.compareAt * quantityMultiplier) : ((it.price || 0) * quantityMultiplier);
      const newP = it.price != null ? calc.newCents : null;
      const secondary = secondaryTextKey ? `<span class="secondary" style="margin-left:auto;color:#666;font-size:.9rem">${it[secondaryTextKey] || ''}</span>` : '';
      const priceHtml = suppressPrice ? '' : (!secondaryTextKey && it.price != null
        ? `<span class="price">${newP != null ? `<span class="old-price">${Utils.formatMoney(old)}</span><span class="new-price">${Utils.formatMoney(newP)}</span>` : `${Utils.formatMoney(old)}`}</span>`
        : secondary);
      const disabledClass = it.disabled ? ' disabled' : '';
      const ariaDisabled = it.disabled ? ' aria-disabled="true"' : '';
      const soldOutBadge = it.disabled ? '<span class="sold-out-badge">Sold Out</span>' : '';
      return `
        <div class="image-option${disabledClass}" role="option" aria-selected="false"${ariaDisabled} data-index="${idx}" data-value="${it.value}">
          ${it.imageUrl ? `<img class="thumb" src="${it.imageUrl}" alt="${it.label}">` : ''}
          <div class="label-wrapper">
            <span class="label">${it.label}</span>
            ${soldOutBadge}
          </div>
          ${priceHtml}
        </div>
      `;
    };

    let filtered = items.slice();
    const renderOptions = () => {
      optionsContainer.innerHTML = filtered.map((it, idx) => renderOptionHtml(it, idx)).join('');
      options = Array.from(optionsContainer.querySelectorAll('.image-option'));
      focusIndex = Math.min(Math.max(focusIndex, 0), Math.max(0, options.length - 1));
      updateFocus();
    };

    // Initial render
    let options = [];
    renderOptions();

    const setOpen = (open) => {
      root.classList.toggle('open', open);
      toggle.setAttribute('aria-expanded', String(open));
      if (open) {
        focusIndex = 0;
        renderOptions();
        setTimeout(() => {
          const firstOpt = menu.querySelector('.image-option');
          if (firstOpt) firstOpt.focus();
        }, 50);
      }
    };

    toggle.addEventListener('click', () => setOpen(!root.classList.contains('open')));

    optionsContainer.addEventListener('click', (e) => {
      const option = e.target.closest('.image-option');
      if (!option) return;
      // Don't allow selecting disabled items
      if (option.classList.contains('disabled')) return;
      const value = option.getAttribute('data-value');
      const item = filtered.find(i => String(i.value) === String(value));
      setSelected(item);
      setOpen(false);
      if (onSelect) onSelect(value);
    });

    // Global outside click (single handler)
    if (!Utils._dropdownOutsideClickBound) {
      document.addEventListener('click', (evt) => {
        const openDropdowns = document.querySelectorAll('.image-dropdown.open');
        openDropdowns.forEach(d => { if (!d.contains(evt.target)) d.classList.remove('open'); });
      });
      Utils._dropdownOutsideClickBound = true;
    }

    // Keyboard navigation
    function updateFocus() {
      options.forEach((opt, i) => opt.setAttribute('aria-selected', i === focusIndex ? 'true' : 'false'));
      const target = options[focusIndex];
      if (target) target.scrollIntoView({ block: 'nearest' });
    }
    function selectFocused() {
      const focused = options[focusIndex];
      if (!focused) return;
      const value = focused.getAttribute('data-value');
      const item = filtered.find(i => String(i.value) === String(value));
      setSelected(item);
      setOpen(false);
      if (onSelect) onSelect(value);
    }
    toggle.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); }
    });
    menu.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.preventDefault(); setOpen(false); toggle.focus(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); focusIndex = Math.min(options.length - 1, focusIndex + 1); updateFocus(); }
      if (e.key === 'ArrowUp') { e.preventDefault(); focusIndex = Math.max(0, focusIndex - 1); updateFocus(); }
      if (e.key === 'Enter') { e.preventDefault(); selectFocused(); }
    });

    // Search behavior
    if (searchable) {
      const input = menu.querySelector('.image-dropdown__search-input');
      const doFilter = () => {
        const q = (input.value || '').toLowerCase();
        filtered = !q ? items.slice() : items.filter(it => (it.label || '').toLowerCase().includes(q));
        focusIndex = 0;
        renderOptions();
      };
      input.addEventListener('input', Utils.debounce(doFilter, 60));
    }

    // initialize empty
    setSelected(null);
    return { 
      get value() { return selected.value; },
      setSelected: (value) => {
        const item = items.find(i => String(i.value) === String(value));
        if (item) setSelected(item);
      }
    };
  }
};

/**
 * State management
 */
class BundleState {
  constructor() {
    this.selections = {
      keyboard: { product: null, variant: null },
      keycaps: { products: new Map(), activeProductId: null }, // productId -> Set of variant IDs
      switches: { product: null, variant: null },
      stabilizers: { product: null, variant: null }
    };
    this.listeners = new Set();
  }

  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notify() {
    this.listeners.forEach(callback => callback(this.selections));
  }

  updateKeyboard(product, variant) {
    this.selections.keyboard = { product, variant };
    this.notify();
  }

  updateKeycaps(productId, variantIds) {
  if (this.selections.keycaps.activeProductId && this.selections.keycaps.activeProductId != productId) {
    this.selections.keycaps.products.clear();
  }
  this.selections.keycaps.activeProductId = productId;

  const normalized = variantIds.map(String); // <--- ensure all are strings
  if (normalized.length === 0) this.selections.keycaps.products.delete(productId);
  else this.selections.keycaps.products.set(productId, new Set(normalized));

  this.notify();
}


  updateCollection(type, product, variant) {
    this.selections[type] = { product, variant };
    this.notify();
  }

  isValid() {
    const { keyboard, keycaps, switches, stabilizers } = this.selections;
    
    // Check required selections
    if (!keyboard.product || !keyboard.variant) return false;
    if (!switches.product || !switches.variant) return false;
    if (!stabilizers.product || !stabilizers.variant) return false;
    
    // Check keycaps
    if (keycaps.products.size === 0) return false;
    
    // Check if at least one keycap selection has a base kit
    for (const [productId, variantIds] of keycaps.products) {
      const product = window.flexibleBundleData.keycapProducts.find(p => p.id == productId);
      if (product) {
        const selectedVariants = product.variants.filter(v => variantIds.has(v.id.toString()));
        if (Utils.hasBaseKit(selectedVariants, product)) {
          return true;
        }
      }
    }
    
    return false;
  }

  hasBaseKitSelected() {
    const { keycaps } = this.selections;
    if (keycaps.products.size === 0) return false;
    for (const [productId, variantIds] of keycaps.products) {
      const product = window.flexibleBundleData.keycapProducts.find(p => p.id == productId);
      if (product) {
        const selected = product.variants.filter(v => variantIds.has(v.id.toString()));
        // If product has a single total variant (not just 1 available), treat as base
        if (product.variants.length === 1) return true;
        if (Utils.hasBaseKit(selected)) return true;
      }
    }
    return false;
  }

  getTotalPrice() {
    let total = 0;
    const { keyboard, keycaps, switches, stabilizers } = this.selections;

    if (keyboard.variant) total += keyboard.variant.price;
    if (switches.variant) total += switches.variant.price * (window.flexibleBundleData.switchQuantity || 3);
    if (stabilizers.variant) total += stabilizers.variant.price * (window.flexibleBundleData.stabilizerQuantity || 1);

    // Add keycap prices
    for (const [productId, variantIds] of keycaps.products) {
      const product = window.flexibleBundleData.keycapProducts.find(p => p.id == productId);
      if (product) {
        product.variants
          .filter(v => variantIds.has(v.id.toString()))
          .forEach(variant => total += variant.price);
      }
    }

    return total;
  }

  getOriginalTotalPrice() {
    let total = 0;
    const { keyboard, keycaps, switches, stabilizers } = this.selections;

    // Use compare_at_price if available, otherwise use price
    if (keyboard.variant) {
      const originalPrice = keyboard.variant.compare_at_price && keyboard.variant.compare_at_price > keyboard.variant.price
        ? keyboard.variant.compare_at_price
        : keyboard.variant.price;
      total += originalPrice;
    }
    
    if (switches.variant) {
      const originalPrice = switches.variant.compare_at_price && switches.variant.compare_at_price > switches.variant.price
        ? switches.variant.compare_at_price
        : switches.variant.price;
      total += originalPrice * (window.flexibleBundleData.switchQuantity || 3);
    }
    
    if (stabilizers.variant) {
      const originalPrice = stabilizers.variant.compare_at_price && stabilizers.variant.compare_at_price > stabilizers.variant.price
        ? stabilizers.variant.compare_at_price
        : stabilizers.variant.price;
      total += originalPrice * (window.flexibleBundleData.stabilizerQuantity || 1);
    }

    // Add keycap prices
    for (const [productId, variantIds] of keycaps.products) {
      const product = window.flexibleBundleData.keycapProducts.find(p => p.id == productId);
      if (product) {
        product.variants
          .filter(v => variantIds.has(v.id.toString()))
          .forEach(variant => {
            const originalPrice = variant.compare_at_price && variant.compare_at_price > variant.price
              ? variant.compare_at_price
              : variant.price;
            total += originalPrice;
          });
      }
    }

    return total;
  }

  getDiscountedPrice() {
    const total = this.getTotalPrice();
    const discountPercent = window.flexibleBundleData.discountPercentage || 15;
    return Utils.roundCents(total * (1 - discountPercent / 100));
  }
}

/**
 * Component for product selection
 */
class ProductSelector {
  constructor(container, type, products, state) {
    this.container = container;
    this.type = type;
    this.products = products;
    this.state = state;
    this.render();
    this.bindEvents();
  }

  render() {
    // Remove skeleton loader
    const skeleton = this.container.querySelector('.skeleton-dropdown');
    if (skeleton) skeleton.remove();
    
    const title = this.type.charAt(0).toUpperCase() + this.type.slice(1);
    
    this.container.innerHTML = `
      <div class="product-selector" data-type="${this.type}">
        <h3 class="selector-title">${title}</h3>
        <div class="form-group">
          <label>Select ${title}</label>
          <div id="${this.type}-dropdown" class="image-dropdown"></div>
        </div>
        <div id="${this.type}-variants" class="variants-container" style="display: none;"></div>
      </div>
    `;
  }

  bindEvents() {
    const discountPercent = window.flexibleBundleData.discountPercentage || 0;
    // filter out products with no available variants (checking quantity for switches/stabilizers)
    const requiredQty = this.type === 'switches' ? (window.flexibleBundleData.switchQuantity || 3) :
                         this.type === 'stabilizers' ? (window.flexibleBundleData.stabilizerQuantity || 1) : 1;
    let availableProducts = this.products.filter(p => (p.variants || []).some(v => Utils.hasEnoughInventory(v, requiredQty)));
    if (this.type === 'keycaps') {
      availableProducts = availableProducts.filter(p => Utils.isQualifyingKeycapProduct(p));
    }
    if (!availableProducts.length) {
      const container = this.container.querySelector(`#${this.type}-dropdown`);
      container.innerHTML = `<div class="image-dropdown__toggle disabled">No ${this.type} available</div>`;
      return;
    }

    const items = availableProducts.map(p => {
      const av = (p.variants || []).filter(v => Utils.hasEnoughInventory(v, requiredQty));
      const single = av.length === 1;
      const minPrice = Math.min(...av.map(v => v.price));
      const maxPrice = Math.max(...av.map(v => v.price));
      const compareMax = Math.max(...av.map(v => v.compare_at_price || 0));
      const discount = window.flexibleBundleData.discountPercentage || 0;
      
      // Get quantity for switches/stabilizers if single variant
      const quantity = single && this.type === 'switches' ? (window.flexibleBundleData.switchQuantity || 1) :
                       single && this.type === 'stabilizers' ? (window.flexibleBundleData.stabilizerQuantity || 1) : 1;
      
      const { newCents: newMin } = Utils.computeOldNew(minPrice, 0, discount, quantity);
      const { newCents: newMax } = Utils.computeOldNew(maxPrice, 0, discount, quantity);
      
      // Show price for single-variant products (including Default Title variants)
      const isSingleVariant = av.length === 1;
      
      // Add quantity to label for single-variant switches/stabilizers
      let productLabel = p.title;
      if (single && (this.type === 'switches' || this.type === 'stabilizers') && quantity > 1) {
        productLabel = `${p.title} (x${quantity})`;
      }

      return {
        value: String(p.id),
        label: productLabel,
        imageUrl: p.featured_image || '',
        // For single variants, use price/compareAt to get proper styling; for multi-variant, suppress price
        price: isSingleVariant ? (minPrice * quantity) : null,
        compareAt: isSingleVariant ? (compareMax * quantity) : null
      };
    });
    const dropdown = this.container.querySelector(`#${this.type}-dropdown`);
    this.dropdownInstance = Utils.buildImageDropdown(dropdown, {
      items,
      placeholder: `Choose ${this.type}...`,
      onSelect: (value) => this.handleProductChange(value),
      searchable: true,
      searchPlaceholder: `Search ${this.type}...`
    });
    
    // Auto-select if there's only one available product
    if (availableProducts.length === 1) {
      const productId = availableProducts[0].id;
      this.dropdownInstance.setSelected(String(productId));
      this.handleProductChange(productId);
    }
  }

  handleProductChange(productId) {
    const product = this.products.find(p => p.id == productId);
    const variantsContainer = this.container.querySelector(`#${this.type}-variants`);
    
    if (!product) {
      variantsContainer.style.display = 'none';
      return;
    }

    // Show variants
    const availableVariants = product.variants.filter(v => v.available);
    const allVariants = product.variants || [];
    
    // If there are no available variants for this collection item, show disabled state
    if (!availableVariants.length) {
      variantsContainer.style.display = 'none';
      this.selectVariant(null, null);
      const dropdownToggle = this.container.querySelector(`#${this.type}-dropdown .image-dropdown__toggle`);
      if (dropdownToggle) dropdownToggle.classList.add('disabled');
      return;
    }
    
    // Show dropdown if there are multiple variants (even if some are sold out)
    if (allVariants.length > 1) {
      const variantDropdownInstance = this.renderVariants(product, variantsContainer);
      variantsContainer.style.display = 'block';
      
      // If only one variant is available, auto-select it but still show the dropdown
      if (availableVariants.length === 1) {
        const onlyAvailable = availableVariants[0];
        if (variantDropdownInstance) {
          variantDropdownInstance.setSelected(String(onlyAvailable.id));
        }
        this.selectVariant(product, onlyAvailable);
      } else {
        // Multiple available variants - show product's featured image while waiting
        this.showProductPreview(product);
      }
    } else {
      // Only one total variant - auto select and hide dropdown
      variantsContainer.style.display = 'none';
      this.selectVariant(product, availableVariants[0]);
    }

    this.container.querySelector('.product-selector').classList.add('active');
  }

  renderVariants(product, container) {
    const discountPercent = window.flexibleBundleData.discountPercentage || 0;
    // Include ALL variants, not just available ones
    const allVariants = product.variants || [];
    // Get quantity multiplier for switches and stabilizers
    const quantity = this.type === 'switches' ? (window.flexibleBundleData.switchQuantity || 1) :
                     this.type === 'stabilizers' ? (window.flexibleBundleData.stabilizerQuantity || 1) : 1;

    const availableVariants = allVariants.filter(v => Utils.hasEnoughInventory(v, quantity));

    // Sort variants: available first, then sold out
    const sortedVariants = [...allVariants].sort((a, b) => {
      const aAvail = Utils.hasEnoughInventory(a, quantity);
      const bAvail = Utils.hasEnoughInventory(b, quantity);
      if (aAvail && !bAvail) return -1;
      if (!aAvail && bAvail) return 1;
      return 0;
    });

    const items = sortedVariants.map(v => {
      let label = v.title;

      // Append quantity multiplier to label for switches and stabilizers
      if ((this.type === 'switches' || this.type === 'stabilizers') && quantity > 1) {
        label = `${label} (x${quantity})`;
      }

      return {
        value: String(v.id),
        label: label,
        imageUrl: v.image || product.featured_image || '',
        price: v.price,
        compareAt: v.compare_at_price || 0,
        discounted: Math.round(v.price * (1 - discountPercent / 100)),
        disabled: !Utils.hasEnoughInventory(v, quantity)
      };
    });
    container.innerHTML = `
      <div class="form-group">
        <label>Select Variant</label>
        <div id="${this.type}-variant-dropdown" class="image-dropdown"></div>
      </div>
    `;
    const variantDropdown = container.querySelector(`#${this.type}-variant-dropdown`);
    // If there is only one variant and it's "Default Title", auto-select silently
    if (items.length === 1 && (!items[0].label || items[0].label.toLowerCase() === 'default title')) {
      const variant = availableVariants[0];
      if (variant) this.selectVariant(product, variant);
      container.parentElement.style.display = 'none';
      return null;
    }
    
    const dropdownInstance = Utils.buildImageDropdown(variantDropdown, {
      items,
      placeholder: 'Choose variant...',
      quantityMultiplier: quantity,
      onSelect: (value) => {
        const variant = allVariants.find(v => String(v.id) === String(value));
        if (variant && variant.available) {
          this.selectVariant(product, variant);
        }
      }
    });
    
    return dropdownInstance;
  }

  showProductPreview(product) {
    // Temporarily update preview with product's featured image
    // Create a temporary variant object with the product's image
    const tempVariant = {
      id: null,
      title: 'Choose variant...',
      price: 0,
      image: product.featured_image,
      featured_image: product.featured_image
    };
    
    if (this.type === 'keyboard') {
      this.state.updateKeyboard(product, tempVariant);
    } else {
      this.state.updateCollection(this.type, product, tempVariant);
    }
  }
  
  selectVariant(product, variant) {
    if (this.type === 'keyboard') {
      this.state.updateKeyboard(product, variant);
    } else {
      this.state.updateCollection(this.type, product, variant);
    }
    
    // Auto-scroll to next step
    this.scrollToNextStep();
  }
  
  scrollToNextStep() {
    const steps = ['keyboard', 'keycaps', 'switches', 'stabilizers'];
    const currentIndex = steps.indexOf(this.type);
    if (currentIndex < steps.length - 1) {
      const nextStep = steps[currentIndex + 1];
      const nextElement = document.querySelector(`[data-step="${nextStep}"]`);
      if (nextElement) {
        setTimeout(() => {
          nextElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    }
  }
}

/**
 * Component for keycap selection with base kit validation
 */
class KeycapSelector {
  constructor(container, products, state) {
    this.container = container;
    this.products = products;
    this.state = state;
    this.render();
    this.bindEvents();
  }

  render() {
    // Remove skeleton loader
    const skeleton = this.container.querySelector('.skeleton-dropdown');
    if (skeleton) skeleton.remove();
    
    this.container.innerHTML = `
      <div class="product-selector" data-type="keycaps">
        <h3 class="selector-title">Keycaps</h3>
        <div class="form-group">
          <label>Select Keycap Set</label>
          <div id="keycaps-dropdown" class="image-dropdown"></div>
        </div>
        <div id="keycaps-kits" class="keycap-kits" style="display: none;">
        </div>
      </div>
    `;
  }

  bindEvents() {
    // Keycaps may be filtered to only qualifying products
    let products = this.products.filter(p => (p.variants || []).some(v => v.available));
    products = products.filter(p => Utils.isQualifyingKeycapProduct(p));
    const dropdown = this.container.querySelector('#keycaps-dropdown');
    if (!products.length) {
      dropdown.innerHTML = `<div class="image-dropdown__toggle disabled">No keycaps available</div>`;
      return;
    }
    // Show prices for single-variant products
    const discount = window.flexibleBundleData.discountPercentage || 0;
    const items = products.map(p => {
      const av = (p.variants || []).filter(v => v.available);
      const isSingleVariant = (p.variants || []).length === 1;
      const minPrice = isSingleVariant ? Math.min(...av.map(v => v.price)) : 0;
      const compareMax = isSingleVariant ? Math.max(...av.map(v => v.compare_at_price || 0)) : 0;
      
      // Strip " - Default Title" from single-variant products
      let label = p.title;
      if (isSingleVariant && label.endsWith(' - Default Title')) {
        label = label.replace(' - Default Title', '');
      }
      
      return {
        value: String(p.id),
        label: label,
        imageUrl: p.featured_image || '',
        price: isSingleVariant ? minPrice : null,
        compareAt: isSingleVariant ? compareMax : null
      };
    });
    this.dropdownInstance = Utils.buildImageDropdown(dropdown, {
      items,
      placeholder: 'Choose keycap set...',
      onSelect: (value) => this.handleProductChange(value),
      searchable: true,
      searchPlaceholder: 'Search keycap sets...'
    });
    
    // Auto-select if there's only one available keycap product
    if (products.length === 1) {
      const productId = products[0].id;
      this.dropdownInstance.setSelected(String(productId));
      this.handleProductChange(productId);
    }
  }

  handleProductChange(productId) {
    const product = this.products.find(p => p.id == productId);
    const kitsContainer = this.container.querySelector('#keycaps-kits');
    
    if (!product) {
      kitsContainer.style.display = 'none';
      return;
    }

    // Check if product has multiple total variants before showing container
    const isSingleVariant = (product.variants || []).length === 1;
    
    this.renderKits(product, kitsContainer);
    
    // Only show the kits container if there are multiple variants
    if (!isSingleVariant) {
      kitsContainer.style.display = 'block';
    }
    
    this.container.querySelector('.product-selector').classList.add('active');
  }

  renderKits(product, container) {
    const allVariants = product.variants || [];
    const available = allVariants.filter(v => v.available);
    
    // Treat single-variant product as base kit even if title is Default Title
    let baseKits = [];
    let childKits = [];
    let sortedVariants = [];
    
    if (available.length === 1 && allVariants.length === 1) {
      baseKits = [available[0]];
      this.state.updateKeycaps(product.id, [String(available[0].id)]);
      sortedVariants = baseKits;
    } else {
      // Include ALL variants, not just available
      baseKits = allVariants.filter(v => v.title && v.title.toLowerCase().includes('base'));
      childKits = allVariants.filter(v => !(v.title && v.title.toLowerCase().includes('base')));
      
      // Sort all variants together: in-stock base, in-stock child, sold-out base, sold-out child
      sortedVariants = [...baseKits, ...childKits].sort((a, b) => {
        const aIsBase = a.title && a.title.toLowerCase().includes('base');
        const bIsBase = b.title && b.title.toLowerCase().includes('base');
        
        // Priority: available base > available child > unavailable base > unavailable child
        if (a.available && !b.available) return -1;
        if (!a.available && b.available) return 1;
        
        // Both same availability - base kits come before child kits
        if (aIsBase && !bIsBase) return -1;
        if (!aIsBase && bIsBase) return 1;
        
        return 0;
      });
    }
    const productHasSingleVariant = (product.variants || []).length === 1;
    
    // Create multi-select dropdown
    container.innerHTML = `
      <div class="form-group">
        <label>Select Kits (Base Kit Required):</label>
        <div id="keycaps-kits-dropdown" class="multi-select-dropdown"></div>
      </div>
    `;

    const dropdownContainer = container.querySelector('#keycaps-kits-dropdown');
    this.buildMultiSelectDropdown(dropdownContainer, product, sortedVariants, productHasSingleVariant);
  }

  buildMultiSelectDropdown(container, product, variants, productHasSingleVariant) {
    const selectedKits = new Set();
    const discountPercent = window.flexibleBundleData.discountPercentage || 0;
    
    container.classList.add('multi-select-dropdown');
    container.setAttribute('role', 'listbox');
    container.setAttribute('aria-multiselectable', 'true');
    
    const isBaseKit = (variant) => {
      return variants.length === 1 || (variant.title && variant.title.toLowerCase().includes('base'));
    };
    
    const hasBaseKitSelected = () => {
      return Array.from(selectedKits).some(id => {
        const variant = variants.find(v => String(v.id) === String(id));
        return variant && isBaseKit(variant);
      });
    };
    
    const updateSelection = () => {
      const selectedCount = selectedKits.size;
      const baseSelected = hasBaseKitSelected();
      
      let displayText = 'Select kits...';
      if (selectedCount > 0) {
        const selectedNames = Array.from(selectedKits).map(id => {
          const variant = variants.find(v => String(v.id) === String(id));
          const showTitle = (productHasSingleVariant || (!variant.title || variant.title.toLowerCase() === 'default title')) ? 'Base' : variant.title;
          return showTitle;
        });
        displayText = selectedNames.join(', ');
      }
      
      toggle.querySelector('.label').textContent = displayText;
      toggle.classList.toggle('has-selection', selectedCount > 0);
      toggle.classList.toggle('has-base', baseSelected);
      
      // Update checkbox availability based on base kit selection
      updateCheckboxStates(baseSelected);
    };
    
    const updateCheckboxStates = (baseSelected) => {
      const checkboxes = optionsContainer.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach(checkbox => {
        const variantId = checkbox.value;
        const variant = variants.find(v => String(v.id) === String(variantId));
        const isBase = isBaseKit(variant);
        const option = checkbox.closest('.multi-select-option');
        const isSoldOut = !variant.available;
        
        // Sold out variants always stay disabled
        if (isSoldOut) {
          checkbox.disabled = true;
          option.classList.add('disabled');
          return;
        }
        
        if (!baseSelected && !isBase) {
          // No base kit selected - disable non-base kits
          checkbox.disabled = true;
          option.classList.add('disabled');
        } else {
          // Base kit is selected or this is a base kit - enable (if not sold out)
          checkbox.disabled = false;
          option.classList.remove('disabled');
        }
      });
    };

    container.innerHTML = `
      <button type="button" class="multi-select-dropdown__toggle" aria-haspopup="listbox" aria-expanded="false">
        <span class="label">Select kits...</span>
        <span class="chev">▾</span>
      </button>
      <div class="multi-select-dropdown__menu" role="listbox">
        <div class="multi-select-dropdown__options"></div>
        <div class="multi-select-dropdown__footer">
          <button type="button" class="multi-select-dropdown__done">Done</button>
        </div>
      </div>
    `;

    const toggle = container.querySelector('.multi-select-dropdown__toggle');
    const menu = container.querySelector('.multi-select-dropdown__menu');
    const optionsContainer = container.querySelector('.multi-select-dropdown__options');
    const doneButton = container.querySelector('.multi-select-dropdown__done');

    // Build menu items
    optionsContainer.innerHTML = variants.map(variant => {
      const c = Utils.computeOldNew(variant.price, variant.compare_at_price || 0, discountPercent);
      let showTitle = (productHasSingleVariant || (!variant.title || variant.title.toLowerCase() === 'default title')) ? 'Base' : variant.title;
      const isBase = variants.length === 1 || (variant.title && variant.title.toLowerCase().includes('base'));
      const isSoldOut = !variant.available;
      const soldOutBadge = isSoldOut ? '<span class="sold-out-badge">Sold Out</span>' : '';

      return `
        <div class="multi-select-option${isBase ? ' base-kit' : ''}${isSoldOut ? ' sold-out' : ''}" role="option" aria-selected="false" data-value="${variant.id}" data-available="${variant.available}">
          <input type="checkbox" id="kit-${variant.id}" value="${variant.id}" class="kit-checkbox-input" ${isSoldOut ? 'disabled' : ''}>
          ${variant.image ? `<img class="thumb" src="${variant.image}" alt="${showTitle}">` : ''}
          <div class="label-wrapper">
            <label for="kit-${variant.id}" class="label">${showTitle}</label>
            ${soldOutBadge}
          </div>
          <span class="price">
            <span class="old-price">${Utils.formatMoney(c.oldCents)}</span>
            <span class="new-price">${Utils.formatMoney(c.newCents)}</span>
          </span>
        </div>
      `;
    }).join('');

    // Toggle dropdown
    const setOpen = (open) => {
      container.classList.toggle('open', open);
      toggle.setAttribute('aria-expanded', String(open));
    };

    toggle.addEventListener('click', (e) => {
      e.preventDefault();
      setOpen(!container.classList.contains('open'));
    });

    // Handle done button
    doneButton.addEventListener('click', (e) => {
      e.preventDefault();
      setOpen(false);
      // Auto-scroll to next step if we have a valid selection
      if (hasBaseKitSelected() && selectedKits.size > 0) {
        this.scrollToNextStep();
      }
    });

    // Handle checkbox changes
    const checkboxes = optionsContainer.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        e.stopPropagation();
        const value = checkbox.value;
        const variant = variants.find(v => String(v.id) === String(value));
        const isBase = isBaseKit(variant);
        
        if (checkbox.checked) {
          selectedKits.add(value);
        } else {
          selectedKits.delete(value);
          
          // If a base kit was deselected, remove all non-base kits
          if (isBase && !hasBaseKitSelected()) {
            // Remove all non-base kits from selection
            Array.from(selectedKits).forEach(selectedId => {
              const selectedVariant = variants.find(v => String(v.id) === String(selectedId));
              if (selectedVariant && !isBaseKit(selectedVariant)) {
                selectedKits.delete(selectedId);
                // Uncheck the corresponding checkbox
                const checkboxToUncheck = optionsContainer.querySelector(`input[value="${selectedId}"]`);
                if (checkboxToUncheck) checkboxToUncheck.checked = false;
              }
            });
          }
        }
        
        updateSelection();
        this.state.updateKeycaps(product.id, Array.from(selectedKits));
      });
    });

    // Handle clicking on option rows (including thumbnails)
    const options = optionsContainer.querySelectorAll('.multi-select-option');
    options.forEach(option => {
      option.addEventListener('click', (e) => {
        // Don't trigger if clicking directly on checkbox or label
        if (e.target.matches('input[type="checkbox"]') || e.target.matches('label')) {
          return;
        }
        e.stopPropagation();
        const checkbox = option.querySelector('input[type="checkbox"]');
        if (checkbox && !checkbox.disabled) {
          checkbox.checked = !checkbox.checked;
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });

    // Prevent menu from closing when clicking inside
    menu.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    // Close on outside click
    if (!Utils._multiSelectOutsideClickBound) {
      document.addEventListener('click', (evt) => {
        const openDropdowns = document.querySelectorAll('.multi-select-dropdown.open');
        openDropdowns.forEach(d => { 
          if (!d.contains(evt.target)) {
            d.classList.remove('open');
            d.querySelector('.multi-select-dropdown__toggle').setAttribute('aria-expanded', 'false');
          }
        });
      });
      Utils._multiSelectOutsideClickBound = true;
    }

    updateSelection();
    
    // Initialize with base kits only enabled
    updateCheckboxStates(false);
  }

  scrollToNextStep() {
    const nextElement = document.querySelector('[data-step="switches"]');
    if (nextElement) {
      setTimeout(() => {
        nextElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }
  
  handleKitChange(productId) {
    const checkboxes = this.container.querySelectorAll(`input[data-product="${productId}"]:checked`);
    const selectedVariantIds = Array.from(checkboxes).map(cb => cb.value);
    this.state.updateKeycaps(productId, selectedVariantIds);
  }
}

/**
 * Bundle preview component
 */
class BundlePreview {
  constructor(container, state) {
    this.container = container;
    this.state = state;
    this.state.subscribe(() => this.render());
    this.render();
  }

  render() {
    const { keyboard, keycaps, switches, stabilizers } = this.state.selections;
    
    this.container.innerHTML = `
      <div class="bundle-preview">
        ${this.renderProductCard('Keyboard', keyboard.product, keyboard.variant, window.flexibleBundleData.placeholders?.keyboard)}
        ${this.renderKeycapCards(keycaps, window.flexibleBundleData.placeholders?.keycaps)}
        ${this.renderProductCard('Switches', switches.product, switches.variant, window.flexibleBundleData.placeholders?.switches, window.flexibleBundleData.switchQuantity, !switches.product ? 'No switches available' : '')}
        ${this.renderProductCard('Stabilizers', stabilizers.product, stabilizers.variant, window.flexibleBundleData.placeholders?.stabilizers, window.flexibleBundleData.stabilizerQuantity, !stabilizers.product ? 'No stabilizers available' : '')}
      </div>
    `;
  }

  renderProductCard(type, product, variant, placeholder, quantity = 1, emptyMessage = 'Not selected') {
    const isEmpty = !product || !variant;
    const title = isEmpty ? `${type}` : product.title;
    const variantText = isEmpty ? emptyMessage : variant.title + (quantity > 1 ? ` (x${quantity})` : '');
    const compareAt = variant?.compare_at_price && variant.compare_at_price > variant.price ? variant.compare_at_price : null;
    const discountPercent = window.flexibleBundleData.discountPercentage || 0;
    const discounted = isEmpty ? null : Math.round(variant.price * Math.max(1, quantity) * (1 - discountPercent/100));
    const price = isEmpty ? '' : Utils.formatMoney(discounted || (variant.price * Math.max(1, quantity)));
    const imageUrl = isEmpty ? (placeholder || '') : (variant.image || product.featured_image || placeholder || '');

    return `
      <div class="product-card">
        ${imageUrl ? `<img class="product-card__image" src="${imageUrl}" alt="${title}">` : ''}
        <div class="product-card__info">
          <div class="product-card__title">${title}</div>
          <div class="product-card__variant">${variantText}</div>
          ${''}
        </div>
      </div>
    `;
  }

  renderKeycapCards(keycaps, placeholder) {
    if (keycaps.products.size === 0) {
      return this.renderProductCard('Keycaps', null, null, placeholder);
    }

    let cards = '';
    for (const [productId, variantIds] of keycaps.products) {
      const product = window.flexibleBundleData.keycapProducts.find(p => p.id == productId);
      if (product && variantIds.size > 0) {
        const variants = product.variants.filter(v => variantIds.has(v.id.toString()));
        const discountPercent = window.flexibleBundleData.discountPercentage || 0;
        let oldSum = 0, newSum = 0;
        variants.forEach(v => {
          const calc = Utils.computeOldNew(v.price, v.compare_at_price || 0, discountPercent, 1);
          oldSum += calc.oldCents; newSum += calc.newCents;
        });
        const variantTitles = variants.map(v => v.title).join(', ');

        const imageUrl = product.featured_image || placeholder || '';
        cards += `
          <div class="product-card">
            ${imageUrl ? `<img class=\"product-card__image\" src=\"${imageUrl}\" alt=\"${product.title}\">` : ''}
            <div class="product-card__info">
              <div class="product-card__title">${product.title}</div>
              <div class="product-card__variant">${variantTitles}</div>
              
            </div>
          </div>
        `;
      }
    }

    return cards;
  }
}

/**
 * Bundle summary component
 */
class BundleSummary {
  constructor(container, state) {
    this.container = container;
    this.state = state;
    this.state.subscribe(() => this.render());
    this.render();
  }

  render() {
    const isValid = this.state.isValid();
    const originalPrice = this.state.getOriginalTotalPrice(); // Price before any discounts
    const totalPrice = this.state.getTotalPrice(); // Current sale price
    const discountedPrice = this.state.getDiscountedPrice(); // Sale price with bundle discount
    const saleDiscount = originalPrice - totalPrice; // Savings from compare_at_price sales
    const bundleDiscount = totalPrice - discountedPrice; // Savings from bundle discount
    const totalSavings = originalPrice - discountedPrice; // Total savings (sale + bundle)
    const discountPercent = window.flexibleBundleData.discountPercentage || 15;
    const hasBase = this.state.hasBaseKitSelected();

    this.container.innerHTML = `
      <div class="bundle-summary">
        <h3 class="bundle-summary-title">Bundle Breakdown</h3>
        ${!this.state.hasBaseKitSelected() && totalPrice > 0 ? this.renderValidationMessage() : ''}
        
        <table class="summary-table">
          ${this.renderLineItems()}
          ${isValid && saleDiscount > 0 ? `
            <tr class="discount-row sale-discount">
              <td>On-Sale Savings</td>
              <td class="discount-value">-${Utils.formatMoney(saleDiscount)}</td>
            </tr>
          ` : ''}
          ${isValid ? `
            <tr class="discount-row bundle-discount">
              <td>Bundle Discount (${discountPercent}%)</td>
              <td class="discount-value">-${Utils.formatMoney(bundleDiscount)}</td>
            </tr>
          ` : ''}
          <tr class="subtotal-row">
            <td>Total</td>
            <td>
              ${hasBase && totalSavings > 0 ? 
                `<span class="old-price">${Utils.formatMoney(originalPrice)}</span> <span class="new-price">${Utils.formatMoney(discountedPrice)}</span>` :
                `${Utils.formatMoney(totalPrice)}`
              }
            </td>
          </tr>
        </table>
        <div class="bundle-actions-wrapper">
          ${hasBase && totalSavings > 0 ? `<div class="savings-message">You're saving ${Utils.formatMoney(totalSavings)}!</div>` : ''}
          <button class="add-to-cart-button btn product-form__cart-submit" 
                  ${!isValid ? 'disabled' : ''} 
                  onclick="flexibleBundle.addToCart()">
            ${isValid ? 'Add Bundle to Cart' : 'Complete Selection to Continue'}
          </button>
        </div>
      </div>
    `;
  }

  renderValidationMessage() {
    return `
      <div class="validation-message">
        At least one keycap selection must include a base kit to qualify for the bundle discount.
      </div>
    `;
  }

  renderLineItems() {
    const { keyboard, keycaps, switches, stabilizers } = this.state.selections;
    
    let rows = '';
    
    // Helper to format item title, stripping " - Default Title" if present
    const formatItemTitle = (productTitle, variantTitle, suffix = '') => {
      let displayTitle = variantTitle === 'Default Title' ? productTitle : `${productTitle} - ${variantTitle}`;
      return displayTitle + suffix;
    };

    // Don't show line item if variant.id is null (temporary preview variant)
    if (keyboard.product && keyboard.variant && keyboard.variant.id) {
      const calc = Utils.computeOldNew(keyboard.variant.price, keyboard.variant.compare_at_price || 0, window.flexibleBundleData.discountPercentage || 0, 1);
      rows += `<tr><td>${formatItemTitle(keyboard.product.title, keyboard.variant.title)}</td><td><span class="old-price">${Utils.formatMoney(calc.oldCents)}</span> <span class="new-price">${Utils.formatMoney(calc.newCents)}</span></td></tr>`;
    }

    for (const [productId, variantIds] of keycaps.products) {
      const product = window.flexibleBundleData.keycapProducts.find(p => p.id == productId);
      if (product) {
        product.variants
          .filter(v => variantIds.has(v.id.toString()))
          .forEach(variant => {
            const calc = Utils.computeOldNew(variant.price, variant.compare_at_price || 0, window.flexibleBundleData.discountPercentage || 0);
            rows += `<tr><td>${formatItemTitle(product.title, variant.title)}</td><td><span class="old-price">${Utils.formatMoney(calc.oldCents)}</span> <span class="new-price">${Utils.formatMoney(calc.newCents)}</span></td></tr>`;
          });
      }
    }

    if (switches.product && switches.variant && switches.variant.id) {
      const qty = window.flexibleBundleData.switchQuantity || 3;
      const calc = Utils.computeOldNew(switches.variant.price, switches.variant.compare_at_price || 0, window.flexibleBundleData.discountPercentage || 0, qty);
      rows += `<tr><td>${formatItemTitle(switches.product.title, switches.variant.title, ` (x${qty})`)}</td><td><span class="old-price">${Utils.formatMoney(calc.oldCents)}</span> <span class="new-price">${Utils.formatMoney(calc.newCents)}</span></td></tr>`;
    }

    if (stabilizers.product && stabilizers.variant && stabilizers.variant.id) {
      const qty = window.flexibleBundleData.stabilizerQuantity || 1;
      const calc = Utils.computeOldNew(stabilizers.variant.price, stabilizers.variant.compare_at_price || 0, window.flexibleBundleData.discountPercentage || 0, qty);
      rows += `<tr><td>${formatItemTitle(stabilizers.product.title, stabilizers.variant.title, ` (x${qty})`)}</td><td><span class="old-price">${Utils.formatMoney(calc.oldCents)}</span> <span class="new-price">${Utils.formatMoney(calc.newCents)}</span></td></tr>`;
    }

    return rows;
  }
}

/**
 * Main FlexibleBundle class
 */
class FlexibleBundle {
  constructor(data) {
    // Filter out products with no available variants
    data.keycapProducts = data.keycapProducts.filter(p => 
      p.variants.some(v => v.available)
    );
    data.keyboardProducts = data.keyboardProducts.filter(p => 
      p.variants.some(v => v.available)
    );
    data.switchProducts = data.switchProducts.filter(p => 
      p.variants.some(v => v.available)
    );
    data.stabilizerProducts = data.stabilizerProducts.filter(p => 
      p.variants.some(v => v.available)
    );
    
    this.data = data;
    this.state = new BundleState();
    
    // Make data globally available
    window.flexibleBundleData = data;
    
    this.initializeComponents();
    // New: initialize stage controller, progress UI, vertical stepper previews, and mobile CTA
    // If vertical stepper exists, prefer that and skip the horizontal wizard
    const hasVertical = !!document.getElementById('v-bundle-stepper');
    if (hasVertical) {
      this.initVerticalStepper();
    } else {
      this.initStageController();
      this.initProgressUI();
    }
    this.initMobileCTA();
  }

  initializeComponents() {
    // Initialize product selectors
    new ProductSelector(
      document.getElementById('keyboard-selector'),
      'keyboard',
      this.data.keyboardProducts,
      this.state
    );

    new KeycapSelector(
      document.getElementById('keycaps-selector'),
      this.data.keycapProducts,
      this.state
    );

    new ProductSelector(
      document.getElementById('switches-selector'),
      'switches',
      this.data.switchProducts,
      this.state
    );

    new ProductSelector(
      document.getElementById('stabilizers-selector'),
      'stabilizers',
      this.data.stabilizerProducts,
      this.state
    );

    // Initialize preview and summary
    new BundlePreview(document.getElementById('bundle-preview'), this.state);
    new BundleSummary(document.getElementById('bundle-summary'), this.state);
  }

  // New: Stage controller (single-step wizard)
  initStageController() {
    // If vertical layout is present, do not run the horizontal stage controller
    if (document.getElementById('v-bundle-stepper')) return;
    this.stepOrder = ['keyboard', 'keycaps', 'switches', 'stabilizers'];
    this.stageEl = document.getElementById('bundle-stage');
    this.stepContainers = this.stepOrder.map(k => document.querySelector(`.bundle-step[data-step="${k}"]`));
    this.prevBtn = document.querySelector('.stage-prev');
    this.nextBtn = document.querySelector('.stage-next');
    this.activeStepIndex = 0;

    const safeIndex = (i) => Math.max(0, Math.min(i, this.stepOrder.length - 1));

    this.goToStep = (index) => {
      this.activeStepIndex = safeIndex(index);
      // Toggle visible step
      this.stepContainers.forEach((el, i) => {
        if (!el) return;
        el.classList.toggle('is-active', i === this.activeStepIndex);
      });
      // Controls and progress UI
      const completeMap = this.computeCompleteMap();
      this.updateStageControls(completeMap);
      this.updateProgressUI(completeMap);
      this.renderProgressSelections();
      // Focus first interactive control in the active step
      const activeEl = this.stepContainers[this.activeStepIndex];
      if (activeEl) {
        const focusable = activeEl.querySelector('.image-dropdown__toggle, button, [role="button"], input, select');
        if (focusable) setTimeout(() => focusable.focus({ preventScroll: true }), 0);
      }
      // Ensure stage is in view on mobile
      if (this.stageEl && window.matchMedia('(max-width: 768px)').matches) {
        this.stageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    // Controls
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goToStep(this.activeStepIndex - 1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => {
      const last = this.activeStepIndex >= this.stepOrder.length - 1;
      if (last) {
        if (this.state.isValid()) this.addToCart();
      } else {
        this.goToStep(this.activeStepIndex + 1);
      }
    });

    // Respond to state changes (no auto-advance)
    const onStateChange = () => {
      const completeMap = this.computeCompleteMap();
      this.updateStageControls(completeMap);
      this.updateProgressUI(completeMap);
      this.renderProgressSelections();
    };

    this.state.subscribe(onStateChange);
    // Initial paint
    this.goToStep(0);
    onStateChange();
  }

  updateStageControls(completeMap) {
    if (!this.prevBtn || !this.nextBtn) return;
    const last = this.activeStepIndex >= this.stepOrder.length - 1;
    const map = completeMap || this.computeCompleteMap();
    const currentKey = this.stepOrder[this.activeStepIndex];
    this.prevBtn.disabled = this.activeStepIndex === 0;
    this.nextBtn.textContent = last ? 'Add Bundle' : 'Next';
    this.nextBtn.disabled = last ? !this.state.isValid() : !map[currentKey];
  }

  computeCompleteMap() {
    const { keyboard, switches, stabilizers } = this.state.selections;
    return {
      keyboard: !!(keyboard.product && keyboard.variant),
      keycaps: this.state.hasBaseKitSelected(),
      switches: !!(switches.product && switches.variant),
      stabilizers: !!(stabilizers.product && stabilizers.variant)
    };
  }

  updateProgressUI(completeMap) {
    const progressEls = Array.from(document.querySelectorAll('#bundle-progress .progress-step'));
    if (!progressEls.length) return;
    progressEls.forEach((el, idx) => {
      const key = this.stepOrder[idx];
      el.classList.toggle('is-complete', !!completeMap[key]);
      el.classList.toggle('is-active', idx === this.activeStepIndex);
      el.setAttribute('aria-selected', idx === this.activeStepIndex ? 'true' : 'false');
    });
  }

  // Show a concise selection summary inside each progress step
  renderProgressSelections() {
    const steps = Array.from(document.querySelectorAll('#bundle-progress .progress-step'));
    if (!steps.length) return;
    const sel = this.state.selections;

    const summaries = {
      keyboard: sel.keyboard.product && sel.keyboard.variant ? `${sel.keyboard.product.title} — ${sel.keyboard.variant.title}` : '',
      keycaps: sel.keycaps && sel.keycaps.product ? `${sel.keycaps.product.title}` : (this.state.hasBaseKitSelected() ? 'Base selected' : ''),
      switches: sel.switches.product && sel.switches.variant ? `${sel.switches.product.title} — ${sel.switches.variant.title}` : '',
      stabilizers: sel.stabilizers.product && sel.stabilizers.variant ? `${sel.stabilizers.product.title}` : ''
    };

    steps.forEach((el, idx) => {
      const key = this.stepOrder[idx];
      let chip = el.querySelector('.step-chip');
      if (!chip) {
        chip = document.createElement('span');
        chip.className = 'step-chip';
        el.appendChild(chip);
      }
      chip.textContent = summaries[key] || '';
      chip.hidden = !summaries[key];
    });
  }

  // New: Progress/stepper UI
  initProgressUI() {
    // If vertical layout is present, skip horizontal progress UI
    if (document.getElementById('v-bundle-stepper')) return;
    const steps = Array.from(document.querySelectorAll('#bundle-progress .progress-step'));
    if (!steps.length) return;
    steps.forEach((el, idx) => {
      el.addEventListener('click', () => this.goToStep(idx));
    });
    // Initial decorate
    this.updateProgressUI(this.computeCompleteMap());
  }

  // Vertical stepper: setup per-step previews in the middle column
  initVerticalStepper() {
    const previewEls = {
      keyboard: document.getElementById('preview-keyboard'),
      keycaps: document.getElementById('preview-keycaps'),
      switches: document.getElementById('preview-switches'),
      stabilizers: document.getElementById('preview-stabilizers')
    };
    this.verticalPreviewEls = previewEls;

    const onStateChange = () => this.renderVerticalPreviews();
    this.state.subscribe(onStateChange);
    this.renderVerticalPreviews();
  }

  renderVerticalPreviews() {
    if (!this.verticalPreviewEls) return;
    const { keyboard, keycaps, switches, stabilizers } = this.state.selections;
    const ph = window.flexibleBundleData.placeholders || {};

    // Keyboard
    this.renderSinglePreview(this.verticalPreviewEls.keyboard, keyboard.product, keyboard.variant, ph.keyboard);
    // Switches
    this.renderSinglePreview(this.verticalPreviewEls.switches, switches.product, switches.variant, ph.switches);
    // Stabilizers
    this.renderSinglePreview(this.verticalPreviewEls.stabilizers, stabilizers.product, stabilizers.variant, ph.stabilizers);

    // Keycaps - show the first selected base kit's variant image
    const kcEl = this.verticalPreviewEls.keycaps;
    if (kcEl) {
      let mainProduct = null;
      let baseKitVariant = null;

      // Find the first selected base kit variant
      for (const [productId, variantIds] of (keycaps.products || new Map())) {
        const product = (window.flexibleBundleData.keycapProducts || []).find(p => p.id == productId);
        if (product) {
          mainProduct = product;
          // Find the first base kit variant among selected variants
          for (const variantId of variantIds) {
            const variant = product.variants.find(v => String(v.id) === String(variantId));
            if (variant) {
              const title = (variant.title || '').toLowerCase();
              // Check if it's a base kit (single variant products count as base, or title includes 'base')
              if (product.variants.length === 1 || title.includes('base') || title.includes('default')) {
                baseKitVariant = variant;
                break;
              }
            }
          }
          if (baseKitVariant) break;
        }
      }

      this.renderSinglePreview(kcEl, mainProduct, baseKitVariant, ph.keycaps);
    }
  }

  renderSinglePreview(container, product, variant, placeholder) {
    if (!container) return;
    
    // Only remove skeleton if we have an actual product/variant (not just placeholder)
    const hasActualContent = (variant && (variant.image || variant.featured_image)) || (product && product.featured_image);
    if (hasActualContent) {
      const skeleton = container.querySelector('.skeleton-preview');
      if (skeleton) skeleton.remove();
    }
    
    container.classList.remove('is-grid');
    const url = (variant && (variant.image || variant.featured_image))
      || (product && product.featured_image)
      || placeholder
      || '';
    container.innerHTML = url ? `<div class="preview-img" style="background-image:url('${url}')"></div>` : '';
  }

  // Mobile CTA - DISABLED (always hidden)
  initMobileCTA() {
    const bar = document.getElementById('bundle-mobile-cta');
    if (bar) {
      bar.hidden = true; // Always keep mobile CTA hidden
    }
  }

  async addToCart() {
    if (!this.state.isValid()) return;

    const button = document.querySelector('.add-to-cart-button');
    button.textContent = 'Adding to Cart...';
    button.disabled = true;

    try {
      const items = this.buildCartItems();
      await this.submitToCart(items);
      window.location.href = '/cart';
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Error adding items to cart. Please try again.');
    } finally {
      button.textContent = 'Add Bundle to Cart';
      button.disabled = false;
    }
  }

  buildCartItems() {
    const { keyboard, keycaps, switches, stabilizers } = this.state.selections;
    const bundleId = Utils.generateBundleId(this.data.bundleId);
    const items = [];

    // Add keyboard
    items.push({
      id: keyboard.variant.id,
      quantity: 1,
      properties: {
        '_Bundle ID': bundleId,
        '_bundle_type': 'keyboard'
      }
    });

    // Add keycaps
    for (const [productId, variantIds] of keycaps.products) {
      for (const variantId of variantIds) {
        items.push({
          id: parseInt(variantId),
          quantity: 1,
          properties: {
            '_Bundle ID': bundleId,
            '_bundle_type': 'keycaps'
          }
        });
      }
    }

    // Add switches
    const switchQty = this.data.switchQuantity || 3;
    items.push({
      id: switches.variant.id,
      quantity: switchQty,
      properties: {
        '_Bundle ID': bundleId,
        '_bundle_type': 'switches',
        '_bundle_item_quantity': switchQty
      }
    });

    // Add stabilizers
    const stabQty = this.data.stabilizerQuantity || 1;
    items.push({
      id: stabilizers.variant.id,
      quantity: stabQty,
      properties: {
        '_Bundle ID': bundleId,
        '_bundle_type': 'stabilizers',
        '_bundle_item_quantity': stabQty
      }
    });

    return items;
  }

  async submitToCart(items) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items })
    });

    if (!response.ok) {
      throw new Error('Failed to add items to cart');
    }

    return response.json();
  }
}

// Global instance
let flexibleBundle = null;

// Fetch all products from collection by fetching the collection JSON
async function fetchAllCollectionProducts(collectionHandle) {
  try {
    const response = await fetch(`/collections/${collectionHandle}/products.json?limit=250`);
    const data = await response.json();
    
    return data.products.map(p => ({
      id: p.id,
      title: p.title,
      handle: p.handle,
      featured_image: p.images[0]?.src || null,
      variants: p.variants.map(v => ({
        id: v.id,
        title: v.title,
        price: Math.round(parseFloat(v.price) * 100), // Convert dollars to cents
        compare_at_price: v.compare_at_price ? Math.round(parseFloat(v.compare_at_price) * 100) : 0,
        available: v.available,
        image: v.featured_image?.src || null
      }))
    }));
  } catch (error) {
    console.error('Failed to fetch collection products:', error);
    return [];
  }
}

// Support being called before or after this file executes
window.initFlexibleBundle = window.initFlexibleBundle || (async (data) => {
    // Fetch all keycap products if collection handle is provided
    if (data._collectionHandle) {
      const allProducts = await fetchAllCollectionProducts(data._collectionHandle);
      if (allProducts.length > 0) {
        data.keycapProducts = allProducts;
        data.totalKeycapCount = allProducts.length;
      }
    }

    flexibleBundle = new FlexibleBundle(data);
    window.flexibleBundle = flexibleBundle; // Make globally accessible
});

// Auto-init if Liquid preloaded data earlier
if (window.flexibleBundleData && !window.flexibleBundle) {
  window.initFlexibleBundle(window.flexibleBundleData);
}